import { calculateTakeRatePercentage } from '@/shared/utils/calculateTakeRatePercentage.ts';
import { groupBy } from '@/shared/utils';
import { Price } from '@schuettflix/conversion';
import { PRODUCT_CATEGORY } from '@schuettflix/interfaces';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters';

const DUMMY_CURRENCY = 'EUR';

export interface BalanceSheetProductProps {
    category: PRODUCT_CATEGORY;
    salesPrice: number;
    purchasePrice: number;
    amount: number;
}

type UseBalanceSheetCalculationsProps = {
    products: BalanceSheetProductProps[];
};

const getTotalPrice = (
    products: UseBalanceSheetCalculationsProps['products'],
    priceType: 'purchasePrice' | 'salesPrice'
): Price => {
    return products.reduce(
        (total, product) => {
            const price = new Price(product[priceType], DUMMY_CURRENCY);
            const productTotal = price.multiply(product.amount);
            return total.add(productTotal);
        },
        new Price(0, DUMMY_CURRENCY)
    );
};

interface BalanceSheetCalculationResult {
    customerPrice: number;
    takeRate: number | null;
    materialPrice: number | null;
    transportPrice: number | null;
    servicePrice: number | null;
    customerPriceString: string;
    materialPriceString: string;
    transportPriceString: string;
    servicePriceString: string;
    takeRateString: string | undefined;
}

export const useBalanceSheetCalculations = ({
    products,
}: UseBalanceSheetCalculationsProps): BalanceSheetCalculationResult => {
    const { formatNumberWithCurrency, formatPercentage } = useFormatters();

    const productsByCategory = groupBy(products, 'category');

    const stats = {
        revenue: 0,
        costs: 0,
    };

    products.map(product => {
        if (Math.sign(product.purchasePrice) === -1) {
            stats.revenue += Math.abs(product.purchasePrice) * product.amount;
        } else {
            stats.costs += product.purchasePrice * product.amount;
        }

        if (Math.sign(product.salesPrice) === -1) {
            stats.costs += Math.abs(product.salesPrice) * product.amount;
        } else {
            stats.revenue += product.salesPrice * product.amount;
        }

        return stats;
    });

    const materialPrice = productsByCategory.MATERIAL
        ? getTotalPrice(productsByCategory.MATERIAL, 'purchasePrice').toNumber()
        : null;
    const transportPrice = productsByCategory.TRANSPORT
        ? getTotalPrice(productsByCategory.TRANSPORT, 'purchasePrice').toNumber()
        : null;
    const serviceProducts = (productsByCategory.MATERIAL_SERVICE ?? []).concat(
        productsByCategory.TRANSPORT_SERVICE ?? []
    );
    const servicePrice = serviceProducts.length ? getTotalPrice(serviceProducts, 'purchasePrice').toNumber() : null;

    const totalSalesPrice = getTotalPrice(products, 'salesPrice');

    const takeRate = calculateTakeRatePercentage(stats.revenue, stats.costs);

    const customerPrice = totalSalesPrice.toNumber();
    return {
        customerPrice,
        takeRate,
        materialPrice,
        transportPrice,
        servicePrice,
        customerPriceString: formatNumberWithCurrency(customerPrice < 0 ? -1 * customerPrice : customerPrice),
        materialPriceString:
            materialPrice !== null
                ? formatNumberWithCurrency(materialPrice < 0 ? -1 * materialPrice : materialPrice)
                : '-',
        transportPriceString:
            transportPrice !== null
                ? formatNumberWithCurrency(transportPrice < 0 ? -1 * transportPrice : transportPrice)
                : '-',
        servicePriceString:
            servicePrice !== null ? formatNumberWithCurrency(servicePrice < 0 ? -1 * servicePrice : servicePrice) : '-',
        takeRateString: takeRate !== null ? formatPercentage(takeRate) : undefined,
    };
};
